import OrderedPair from "@/models/OrderedPair";
import SiteReference from "@/models/SiteReference";
import TimeSeries from "@/models/TimeSeries";

/**
 * Return a (shallow-copy) new time series from `overlaySeries`, where the time scale matches
 * `originalSeries`.
 *
 * e.g. Given a full year time series of historical discharge data, find the subset of that
 *      series that overlaps with the past week's discharge data so we can show them both in
 *      a graph of the past week
 *
 * @param originalSeries
 * @param overlaySeries
 */
// TODO: WTF!
// - remove the .data dependency on this
export const overlayTimeSeries = (originalSeries: TimeSeries, overlaySeries: TimeSeries): Array<OrderedPair> => {

  if (!originalSeries.data || originalSeries.data.length === 0) {
    return [];
  }

  let result = [];
  if (originalSeries.data.length === 1) {
    // TODO handle
    if (originalSeries.data[0].x === overlaySeries.data[0].x) {
      result.push(overlaySeries.data[0]);
    }
  }
  if (originalSeries.data.length >= 2) {
    const lowerBound = originalSeries.data[0].x;
    const upperBound = originalSeries.data[originalSeries.data.length - 1].x;
    result = overlaySeries.data.filter((dataPoint: OrderedPair) => {


      // FIXME fuck...
      //
      // The bug is right here...
      //
      // Fri Sep 03 2021 20:30:00 GMT-0600 (Mountain Daylight Time) vs "2021-01-01T19:00:00.000Z"
      //
      // Who knows why one is the other. seriously. Fuck I'm feeling very discouraged. Yikes.
      return dataPoint.x >= lowerBound && dataPoint.x <= upperBound;
    })
  }

  return result;
}

const areSetsEqual = (a: Set<any>, b: Set<any>) => a.size === b.size && [...a].every(value => b.has(value));


// https://help.waterdata.usgs.gov/codes-and-parameters/instantaneous-and-daily-value-status-codes
// const qualifiersDecoderMap = {
//   "Bkw" : "Value is affected by backwater at the measurement site.",
//   "Dis" :	"Record has been discontinued at the measurement site."
//   Dry	Dry condition exists at the measurement site.
//   Eqp	Value affected by equipment malfunction.
//   Fld	Value is affected by flooding conditions at the measurement site.
//   Ice	Value is affected by ice at the measurement site.
//   Mnt	Site under going maintenance.
//   Pr	Parameter only partially monitored over specific range of values or time periods.
//   Pmp	Value is affected by pumping at time of measurement.
//   Rat	Rating being developed.
//   Ssn	Parameter monitored seasonally.
//   Tst	Value is affected by artificial test condition.
//   Zfl	Zero flow condition present at the measurement site.
//   ***	Value unavailable.
//   --	Parameter not determined
// }

const qualifiersDecoderMap: Record<string, string> = {
  "Bkw" : "Backwater",
  "Dis" :	"Discontinued",
  "Dry" :	"Dry",
  "Eqp" : "Equipment Malfunction",
  "Fld" : "Flood",
  "Ice" : "Ice",
  "Mnt" : "Site Under Maintenance",
  "Pr"  : "Partially Monitored",
  "Pmp" : "Pumping",
  "Rat" : "Rating Being Developed",
  "Ssn" : "Monitored Seasonally",
  "Tst" : "Test Measurement",
  "Zfl" : "Zero Flow Condition",
  "***" : "Value Unavailable",
  "--"  : "Parameter Not Determined"
}

export const decodeQualifiers = (qualifiers: Array<string>) => {

  const qualifiersClone = [... qualifiers];
  // First get rid of the qualifier "P" which is in every datapoint
  const pIndex = qualifiers.findIndex((el) => el === "P");
  if (pIndex !== -1) {
    qualifiersClone.splice(pIndex, 1);
  }

  // Map each qualifier code to its label
  const qualifierLabels = qualifiersClone.map(q => qualifiersDecoderMap[q] || "Parameter Not Determined");

  // Join with a semi-colon and return
  return qualifierLabels.join("; ");
}

/**
 * see echarts docs here https://echarts.apache.org/examples/en/editor.html?c=line-sections
 *
 * Generate the echarts config object needed to create marked areas
 *
 * TODO: consolidate with echarts builder utils
 */
export const generateMarkedAreasEchartsConfig = (series: Record<string, any>) => {
  const markedAreas = [];
  let inMarkedArea = false;
  let currentBeginningMark;
  let currentEndingMark;
  let currentQualifiers;
  const dataPoints = series.dataPoints;

  for (const dataPoint of dataPoints) {

    // Invalid data points are "-999999" + have info about why they are invalid
    if (dataPoint.y === "-999999") {
      if (inMarkedArea === false) {
        inMarkedArea = true;
        currentQualifiers = dataPoint.qualifiers;
        currentBeginningMark =
          {
            name: decodeQualifiers(currentQualifiers),
            xAxis: dataPoint.x
          };

          // Set the end mark as the same, just in case there's only one data point
          currentEndingMark = {
            xAxis: dataPoint.x
          };
        }
      else {
        // Check whether the qualifiers changed requiring a change in marked area
        const currentQualifiersSet = new Set(currentQualifiers);
        const theseQualifiersSet = new Set(dataPoint.qualifiers);

        if (areSetsEqual(currentQualifiersSet, theseQualifiersSet)){

          // If we're here, qualifiers haven't changed. Update ending mark since when
          // we leave the marked area we won't have the last data point
          currentEndingMark = {
            xAxis: dataPoint.x
          };
        }
        else {
          // Qualifiers changed... Push the current marked area...
          markedAreas.push(
            [ { ...currentBeginningMark }, { ...currentEndingMark }]
          );

          // And then start a new marked area
          currentQualifiers = dataPoint.qualifiers;
          currentBeginningMark =
          {
            name: decodeQualifiers(currentQualifiers),
            xAxis: dataPoint.x
          };
          // Set the end mark as the same, just in case there's only one data point
          currentEndingMark = {
            xAxis: dataPoint.x
          };
        }
      }
    }
    else if (inMarkedArea) {
      // If we were in a marked area... close it out
      markedAreas.push(
        [ { ...currentBeginningMark }, { ...currentEndingMark }]
      );
      inMarkedArea = false;
    }
  }

  // If we're still in a marked area, it means the marked area extended to the end
  if (inMarkedArea) {
    markedAreas.push(
      [ { ...currentBeginningMark }, { ...currentEndingMark }]
    );
  }
  return markedAreas;
}

/**
 * Builds the `data` and `markArea` parts of the echarts series config object
 * - finds areas with invalid values (-999999)
 * - replaces those in time series with the reason why it's invalid
 * - builds markArea sections to shade them on the graph
 *   (see https://echarts.apache.org/examples/en/editor.html?c=line-sections)
 *
 * @returns
 *
 * {
 *   data: [[2023-10-14T08:25:00.000Z, "7"], ...]
 *   markArea: {
 *     data: [
 *       [
 *         name: "Discontinued",
 *         xAxis: "2023-10-14T08:25:00.000Z"
 *       ]
 *     ]
 *   }
 * }
 */
export const buildEchartsSeriesConfig = (series: Record<string, any>) => {
  const markedAreas = [];
  let inMarkedArea = false;
  let currentBeginningMark;
  let currentEndingMark;
  let currentDecodedQualifiers: string = "";
  const dataPoints = series.dataPoints;

  // The `data` object
  const echartsSeriesData = [];

  for (const dataPoint of dataPoints) {

    // Make the echarts data point
    const echartsSeriesDatum = [dataPoint.x, dataPoint.y];

    // Invalid data points are "-999999" + have info about why they are invalid
    // - they signal we need to either create, or append, a marked area
    // - and that we need to replace the datapoint with the reason for the invalid value
    if (echartsSeriesDatum[1] === "-999999") {
      if (inMarkedArea === false) {
        inMarkedArea = true;
        currentDecodedQualifiers = decodeQualifiers(dataPoint.qualifiers);
        echartsSeriesDatum[1] = currentDecodedQualifiers;
        currentBeginningMark =
          {
            name: currentDecodedQualifiers,
            xAxis: dataPoint.x
          };

          // Set the end mark as the same, just in case there's only one data point
          currentEndingMark = {
            xAxis: dataPoint.x
          };
        }
      else {
        // Check whether the qualifiers changed requiring a change in marked area
        const newDecodedQualifiers = decodeQualifiers(dataPoint.qualifiers);
        if (currentDecodedQualifiers === newDecodedQualifiers){

          // If we're here, qualifiers haven't changed. Update ending mark since when
          // we leave the marked area we won't have the last data point
          currentEndingMark = {
            xAxis: dataPoint.x
          };
        }
        else {
          // Qualifiers changed... Push the current marked area...
          markedAreas.push(
            [ { ...currentBeginningMark }, { ...currentEndingMark }]
          );

          // And then start a new marked area
          currentDecodedQualifiers = decodeQualifiers(dataPoint.qualifiers);
          currentBeginningMark =
          {
            name: currentDecodedQualifiers,
            xAxis: dataPoint.x
          };
          // Set the end mark as the same, just in case there's only one data point
          currentEndingMark = {
            xAxis: dataPoint.x
          };
        }
        // Update the datapoint with the decoded qualifier
        echartsSeriesDatum[1] = currentDecodedQualifiers;
      }
    }
    else if (inMarkedArea) {
      // If we were in a marked area... close it out
      markedAreas.push(
        [ { ...currentBeginningMark }, { ...currentEndingMark }]
      );
      inMarkedArea = false;
    }

    // Add datapoint to what will become `data`
    echartsSeriesData.push(echartsSeriesDatum);
  }

  // If we're still in a marked area, it means the marked area extended to the end. Finalize it.
  if (inMarkedArea) {
    markedAreas.push(
      [ { ...currentBeginningMark }, { ...currentEndingMark }]
    );
  }
  return {
    data: echartsSeriesData,
    markArea: {
      data: markedAreas
    }
  };
}

export const overlayHistoricalDataOntoTimeSeries = (originalSeries: TimeSeries, historicalData: Record<string, any>): Array<OrderedPair> => {

  if (!originalSeries.data || originalSeries.data.length === 0) {
    return [];
  }

  let result = [];

  // Create a new time series with a 1:1 corresponce of data
  const historicalOverlay = originalSeries.data.map((pair: OrderedPair) => {

    const dateStr = new String(pair.x.getDate()).padStart(2, '0');
    const monthStr = new String(pair.x.getMonth() + 1).padStart(2, '0');

    const monthDateStr = `${monthStr}${dateStr}`;

    const historicalValue = historicalData[monthDateStr]
    return {
      x: pair.x,
      y: historicalValue,
      qualifiers: ''
    }
  });

  return historicalOverlay;
}


export const sortSiteReferences = (siteRefs: Array<SiteReference>) => {
  return siteRefs.sort((siteRefA, siteRefB) => {
      if (siteRefA.siteName < siteRefB.siteName) {
        return -1;
      }
      if (siteRefA.siteName > siteRefB.siteName) {
        return 1;
      }
      // a must be equal to b
      return 0;
  });
}

/**
 * Find the set intersection of the given array of arrays of site references
 *
 * TODO unit tests plz
 */
export const findSiteRefSetIntersection = (siteRefArrays: Array<Array<SiteReference>>) => {

  if (siteRefArrays.length === 0) {
    return [];
  }

  // Get a set that's the intersection of the site numbers from each result (Set's don't work well with
  // objects, so instead we use a primitive, string)
  let matchedSiteNumbers = new Set(siteRefArrays[0].map(siteRef => siteRef.siteNumber));
  for (let i = 1; i < siteRefArrays.length; ++i) {
    const currentSetSiteNumbers = new Set(siteRefArrays[i].map(siteRef => siteRef.siteNumber));
    matchedSiteNumbers = new Set(
      [...matchedSiteNumbers].filter(siteNumber => currentSetSiteNumbers.has(siteNumber))
    );
  }

  // Get matching site refs
  let result = siteRefArrays[0].filter(siteRef => matchedSiteNumbers.has(siteRef.siteNumber));

  // Sort and return
  return sortSiteReferences(result);
}

export const celciusToFahrenheit = (degreesCelcius: number): number => {
  return (1.8 * degreesCelcius) + 32;
}

/**
 * Parses the USGS RDB file format described by https://waterservices.usgs.gov/rest/Site-Service.html#CORS
 *
 * File will have an unknown number of comment lines starting with '#' followed by tab-delimited data,
 * where the
 * - row 0 of data represents field name
 * - row 1 of data represents data type
 * - rows 2..n of data represent USGS site information for n sites
 *
 * @return  an array of POJSOs where each object represents a row of data, with field values keyed
 *          by their respective column headers
 */
export const parseUsgsRdb = (reqData: any): any => {

  // Split req.data string into an array by newline character
  const lines = reqData.split("\n");

  // Iterate through lines until the line we're on does not have a "#"
  let index = 0;
  let columnHeadersFound = false;

  while (!columnHeadersFound && index < lines.length) {
    let line = lines[index];
    if (line.indexOf('#') === 0) {
      ++index;
    } else {
      columnHeadersFound = true;
    }
  }

  // Put lines of data into an array (minus the line with just a newline
  // at the end
  let rawDataLines = lines.slice(index, lines.length - 1);

  // At this point:
  // -  index 0 contains column headers
  // -  index 1 shows the data type
  // -  indices 2..n contain map values, one row per station
  const mapKeyIndex = 0;
  const firstSiteRowIndex = 2;

  // Split tab-delimited strings into arrays
  const tabDelimitedLines = rawDataLines.map((line: string) => {
    return line.split('\t');
  });

  // Build POJSOs representing each site
  const columnHeaders = tabDelimitedLines[mapKeyIndex];
  const dataLines = tabDelimitedLines.slice(firstSiteRowIndex);
  const data = dataLines.map((dataLine: Array<String>) => {
    const row: Record<string, any> = {};

    // Map each array of values to a POJSO of key/value pairs, keyed by the values' respective column headers
    for (let i = 0; i < columnHeaders.length; ++i) {
      const columnHeader = columnHeaders[i];
      const value = dataLine[i];
      row[columnHeader] = value;
    }
    return row;
  });

  return data;
}